import React, { useState, useEffect, useRef } from "react";
import AskedOfYou from "../../components/AskedOfYou.jsx";
import Wordmark from "../../components/Wordmark.jsx";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth.jsx";
import UserChip from "../../components/UserChip.jsx";
import ClientNameBlock from "../../components/ClientNameBlock.jsx";
import AccessPanel from "../../components/AccessPanel.jsx";

/**
 * Family dashboard — the owner's home (§4.2).
 *
 * Deliberately flatter than the coordinator list: a household has one or a few
 * people, not a caseload. So the empty state is the important state, and it is
 * a single clear invitation rather than a "no results" shrug.
 *
 * Per-client capabilities arrive on each card from the API rather than being
 * inferred from the signed-in role, because they differ per person — see
 * app/services/permissions.py.
 *
 * NOTE ON URLS: the build links point at /coordinator/clients/... That word is
 * wrong for a parent, and it is deferred on purpose. Those pages carry dozens
 * of internal links to that prefix, and rewriting them would mean touching the
 * agency flow, which §7 says to leave alone. The URL is cosmetic and invisible
 * in normal use; it gets fixed alongside the in-page copy pass, as one change
 * to one vocabulary rather than two half-changes.
 */

function Topbar({ user, onLogout }) {
  return (
    <div style={tb.bar}>
      <Wordmark />
      <UserChip user={user} onLogout={onLogout} />
    </div>
  );
}

// ── Add-person modal ─────────────────────────────────────────────────────────

function AddPersonModal({ onClose, onCreate }) {
  const [form, setForm]   = useState({ display_name: "", full_name: "", pronouns: "", location_display: "" });
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState(null);
  const firstRef = useRef();

  useEffect(() => { firstRef.current?.focus(); }, []);

  function set(k, v) { setForm(p => ({ ...p, [k]: v })); }

  async function submit() {
    if (!form.display_name.trim()) { setError("Please enter the name you call them."); return; }
    setSaving(true); setError(null);
    try {
      await onCreate({
        display_name:     form.display_name.trim(),
        full_name:        form.full_name.trim() || form.display_name.trim(),
        pronouns:         form.pronouns.trim() || null,
        location_display: form.location_display.trim() || null,
      });
    } catch (e) {
      setError(e.message); setSaving(false);
    }
  }

  return (
    <div style={m.overlay} onKeyDown={e => e.key === "Escape" && onClose()}>
      <div style={m.box}>
        <div style={m.header}>
          <span style={m.title}>Who are you setting this up for?</span>
          <button style={m.close} onClick={onClose} aria-label="Close">×</button>
        </div>

        <label style={m.label}>
          What you call them
          <input ref={firstRef} style={m.input} value={form.display_name}
                 onChange={e => set("display_name", e.target.value)}
                 placeholder="Daniel" />
        </label>

        <label style={m.label}>
          Their full name
          <input style={m.input} value={form.full_name}
                 onChange={e => set("full_name", e.target.value)}
                 placeholder="Daniel D." />
        </label>

        <div style={m.row}>
          <label style={{ ...m.label, flex: 1 }}>
            Pronouns
            <input style={m.input} value={form.pronouns}
                   onChange={e => set("pronouns", e.target.value)} placeholder="he/him" />
          </label>
          <label style={{ ...m.label, flex: 1 }}>
            Where they live
            <input style={m.input} value={form.location_display}
                   onChange={e => set("location_display", e.target.value)} placeholder="Petaluma" />
          </label>
        </div>

        {error && <div style={m.error}>{error}</div>}

        <div style={m.footer}>
          <button style={m.cancelBtn} onClick={onClose} disabled={saving}>Cancel</button>
          <button style={{ ...m.createBtn, ...(saving ? m.btnDisabled : {}) }}
                  onClick={submit} disabled={saving}>
            {saving ? "Creating…" : "Continue"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

export default function FamilyClients() {
  const { user, logout, apiFetch } = useAuth();
  const navigate = useNavigate();
  const [data, setData]     = useState(null);
  const [error, setError]   = useState(null);
  const [adding, setAdding] = useState(false);
  // One panel, one question: who can see this profile. Previously two buttons
  // and two modals, split along a line the plumbing cared about and the person
  // using it did not.
  const [managing, setManaging] = useState(null);

  async function load() {
    try {
      const r = await apiFetch("/api/family/clients");
      if (!r.ok) throw new Error("We couldn't load your profiles just now.");
      setData(await r.json());
    } catch (e) {
      // A 401 has already cleared the user inside apiFetch; the route guard
      // will send them to sign-in on the next render, so showing an error here
      // would only flash a message they can't act on.
      if (!e.sessionEnded) setError(e.message);
    }
  }

  useEffect(() => { load(); }, []);

  async function handleLogout() {
    await logout();
    navigate("/", { replace: true });
  }

  async function handleCreate(body) {
    const r = await apiFetch("/api/clients", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    const d = await r.json();
    if (!r.ok) throw new Error(d.error || "Could not create the profile");
    navigate(`/coordinator/clients/${d.id}/quick-start`);
  }

  const first    = user?.display_name?.split(" ")[0] || "there";
  const clients  = data?.clients || [];
  const canAdd   = (data?.capabilities || []).includes("create_client");
  const isEmpty  = data && clients.length === 0;

  return (
    <div style={st.page}>
      <Topbar user={user} onLogout={handleLogout} />
      <div style={st.container}>

        {isEmpty ? (
          <div style={st.empty}>
            <h1 style={st.heading}>Welcome, {first}</h1>
            <AskedOfYou />
            <p style={st.emptyBody}>
              Everyone who steps in to help deserves to know the person, not just
              the schedule. Start by telling us who you're setting this up for —
              you can add as much or as little as you like, whenever you like.
            </p>
            {canAdd && (
              <button style={st.primaryBtn} onClick={() => setAdding(true)}>
                Add the person you care for
              </button>
            )}
          </div>
        ) : (
          <>
            <div style={st.headRow}>
              <div>
                <h1 style={st.heading}>Welcome, {first}</h1>
            <AskedOfYou />
                <p style={st.sub}>
                  {clients.length === 1
                    ? "One profile in your care."
                    : `${clients.length} profiles in your care.`}
                </p>
              </div>
              {canAdd && (
                <button style={st.addBtn} onClick={() => setAdding(true)}>+ Add someone</button>
              )}
            </div>

            <div style={st.grid}>
              {clients.map(c => {
                const caps = c.capabilities || [];
                const canEdit       = caps.includes("edit_client");
                const canContribute = caps.includes("contribute_client");
                const canManage     = caps.includes("manage_client");
                const meta = [c.age, c.location].filter(Boolean).join(" · ");
                // Three audiences, three destinations. A Contributor used to
                // land on the read-only profile exactly like a Viewer, because
                // the card only asked "can you edit?" — so Ruth could reach the
                // interview by permission but had no link that took her there.
                const target = canEdit
                  ? `/coordinator/clients/${c.id}/edit`
                  : `/clients/${c.id}`;
                const openLabel = canEdit ? "Continue →"
                                : canContribute ? "See the profile →"
                                : "Open →";
                return (
                  <div key={c.id} style={st.cardWrap}>
                  <Link to={target} style={st.card}>
                    <div style={st.cardHead}>
                      <div style={st.avatar}>{c.display_name[0]}</div>
                      <div>
                        <ClientNameBlock displayName={c.display_name} fullName={c.full_name}
                                         nameStyle={st.name} />
                        {meta && <div style={st.meta}>{meta}</div>}
                      </div>
                    </div>
                    {c.hero_oneliner && <div style={st.body}>{c.hero_oneliner}</div>}
                    <div style={st.cardFoot}>
                      <span style={st.counts}>
                        {c.section_counts?.live || 0} of 10 sections ready
                      </span>
                      <span style={st.arrow}>{openLabel}</span>
                    </div>
                  </Link>
                  {canManage && (
                    <button style={st.shareBtn} onClick={() => setManaging(c)}>
                      Give someone access
                    </button>
                  )}
                  {/* The Contributor's door. They were invited BECAUSE they know
                      something; this is where they say it. Shown only to people
                      who can add but not restructure — an Organizer reaches the
                      same place through the editor hub. */}
                  {!canEdit && canContribute && (
                    <div style={st.actions}>
                      <Link to={`/coordinator/clients/${c.id}/interviews`}
                            style={st.contributeBtn}>
                        Answer questions
                      </Link>
                      <Link to={`/family/clients/${c.id}/photos`}
                            style={st.contributeBtn}>
                        Add photos
                      </Link>
                    </div>
                  )}
                  </div>
                );
              })}
            </div>
          </>
        )}

        {error && <p style={st.err}>{error}</p>}
        {!data && !error && <p style={st.sub}>Loading…</p>}
      </div>

      {adding && <AddPersonModal onClose={() => setAdding(false)} onCreate={handleCreate} />}
      {managing && <AccessPanel client={managing} onClose={() => setManaging(null)} />}
    </div>
  );
}

const tb = {
  bar:      { background: "var(--color-surface)", borderBottom: "1px solid var(--color-border)", padding: "12px 32px", display: "flex", alignItems: "center", justifyContent: "space-between" },
  wordmark: { fontWeight: "var(--weight-medium)", fontSize: 17, letterSpacing: "-0.3px", color: "var(--color-text-primary)", display: "flex", alignItems: "center", gap: 6 },
  dot:      { display: "inline-block", width: 8, height: 8, borderRadius: "50%", background: "var(--color-accent)" },
};

const st = {
  page:       { minHeight: "100dvh", background: "var(--color-bg-subtle)" },
  container:  { maxWidth: 860, margin: "0 auto", padding: "48px 32px 80px" },
  headRow:    { display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16, marginBottom: 28 },
  heading:    { fontSize: "var(--text-2xl)", fontWeight: "var(--weight-medium)", letterSpacing: "-0.5px", color: "var(--color-text-primary)" },
  sub:        { fontSize: "var(--text-sm)", color: "var(--color-text-secondary)", marginTop: 4 },
  empty:      { background: "var(--color-surface)", border: "1px solid var(--color-border)", borderRadius: "var(--radius-xl)", padding: "48px 40px", textAlign: "center", maxWidth: 540, margin: "24px auto" },
  emptyBody:  { fontSize: "var(--text-base)", color: "var(--color-text-secondary)", lineHeight: "var(--leading-normal)", margin: "14px auto 28px", maxWidth: 420 },
  primaryBtn: { background: "var(--color-accent)", color: "#fff", border: "none", borderRadius: "var(--radius-md)", padding: "12px 22px", fontSize: "var(--text-base)", fontWeight: "var(--weight-semibold)", cursor: "pointer", font: "inherit" },
  addBtn:     { background: "var(--color-accent)", color: "#fff", border: "none", borderRadius: "var(--radius-md)", padding: "9px 16px", fontSize: "var(--text-sm)", fontWeight: "var(--weight-medium)", cursor: "pointer", flexShrink: 0 },
  cardWrap:   { display: "flex", flexDirection: "column", gap: 8 },
  actions:    { display: "flex", gap: 8 },
  contributeBtn: { flex: 1, display: "block", textAlign: "center", background: "var(--color-accent)", color: "#fff", textDecoration: "none", borderRadius: "var(--radius-md)", padding: "11px 14px", fontSize: "var(--text-sm)", fontWeight: "var(--weight-medium)" },
  shareBtn:   { flex: 1, background: "none", border: "1px solid var(--color-border)", borderRadius: "var(--radius-md)", padding: "8px 12px", fontSize: "var(--text-sm)", color: "var(--color-text-secondary)", cursor: "pointer", font: "inherit" },
  grid:       { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 18 },
  card:       { background: "var(--color-surface)", border: "1px solid var(--color-border)", borderRadius: "var(--radius-xl)", padding: 28, textDecoration: "none", color: "var(--color-text-primary)", display: "block" },
  cardHead:   { display: "flex", alignItems: "center", gap: 16, marginBottom: 14 },
  avatar:     { width: 56, height: 56, borderRadius: "50%", background: "var(--color-accent-subtle)", color: "var(--color-accent)", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: "var(--weight-medium)", fontSize: 20, flexShrink: 0 },
  name:       { fontSize: "var(--text-base)", fontWeight: "var(--weight-medium)", color: "var(--color-text-primary)" },
  meta:       { fontSize: "var(--text-xs)", color: "var(--color-text-tertiary)", marginTop: 3 },
  body:       { fontSize: "var(--text-sm)", color: "var(--color-text-secondary)", lineHeight: "var(--leading-normal)", paddingTop: 14, borderTop: "1px solid var(--color-border)" },
  cardFoot:   { marginTop: 14, display: "flex", alignItems: "center", justifyContent: "space-between" },
  counts:     { fontSize: "var(--text-xs)", color: "var(--color-text-tertiary)" },
  arrow:      { color: "var(--color-accent)", fontWeight: "var(--weight-medium)", fontSize: "var(--text-sm)" },
  err:        { color: "var(--color-critical)", fontSize: "var(--text-sm)", marginTop: 16 },
};

const m = {
  overlay:     { position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100 },
  box:         { background: "var(--color-surface)", borderRadius: "var(--radius-xl)", padding: "var(--space-8)", width: 480, maxWidth: "calc(100vw - 48px)", display: "flex", flexDirection: "column", gap: "var(--space-4)" },
  header:      { display: "flex", alignItems: "center", justifyContent: "space-between" },
  title:       { fontSize: "var(--text-lg)", fontWeight: "var(--weight-semibold)", color: "var(--color-text-primary)" },
  close:       { background: "none", border: "none", fontSize: 22, color: "var(--color-text-tertiary)", cursor: "pointer", lineHeight: 1, padding: 0 },
  label:       { display: "flex", flexDirection: "column", gap: "var(--space-1)", fontSize: "var(--text-sm)", color: "var(--color-text-secondary)" },
  input:       { fontSize: "var(--text-sm)", border: "1px solid var(--color-border)", borderRadius: "var(--radius-sm)", padding: "var(--space-2) var(--space-3)", color: "var(--color-text-primary)", background: "var(--color-bg-base)", width: "100%", boxSizing: "border-box", fontFamily: "inherit" },
  row:         { display: "flex", gap: "var(--space-3)" },
  footer:      { display: "flex", justifyContent: "flex-end", gap: "var(--space-3)", paddingTop: "var(--space-2)" },
  error:       { fontSize: "var(--text-sm)", color: "var(--color-critical)", background: "var(--color-critical-bg)", borderRadius: "var(--radius-sm)", padding: "var(--space-2) var(--space-3)" },
  cancelBtn:   { fontSize: "var(--text-sm)", background: "none", border: "1px solid var(--color-border)", borderRadius: "var(--radius-md)", padding: "var(--space-2) var(--space-4)", color: "var(--color-text-secondary)", cursor: "pointer", font: "inherit" },
  createBtn:   { fontSize: "var(--text-sm)", fontWeight: "var(--weight-medium)", background: "var(--color-accent)", color: "var(--color-text-on-accent)", border: "none", borderRadius: "var(--radius-md)", padding: "var(--space-2) var(--space-5)", cursor: "pointer", font: "inherit" },
  btnDisabled: { opacity: 0.5, cursor: "not-allowed" },
};
